import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Send, Paperclip, ImagePlus, X, Loader2, Plus, Trash2,
  MessageSquare, Bot, User, Download, ChevronLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MarkdownText({ text }) {
  if (!text) return null;
  const lines = text.split('\n');
  return (
    <div className="space-y-1 text-sm leading-relaxed">
      {lines.map((line, i) => {
        if (line.startsWith('### ')) return <h4 key={`h4-${i}`} className="font-bold text-slate-800 mt-2">{line.slice(4)}</h4>;
        if (line.startsWith('## ')) return <h3 key={`h3-${i}`} className="font-bold text-slate-800 text-base mt-3">{line.slice(3)}</h3>;
        if (line.startsWith('# ')) return <h2 key={`h2-${i}`} className="font-bold text-slate-900 text-lg mt-3">{line.slice(2)}</h2>;
        if (line.startsWith('- ') || line.startsWith('* ')) return <li key={`li-${i}`} className="ml-4 list-disc">{formatInline(line.slice(2))}</li>;
        if (/^\d+\.\s/.test(line)) return <li key={`ol-${i}`} className="ml-4 list-decimal">{formatInline(line.replace(/^\d+\.\s/, ''))}</li>;
        if (line.trim() === '') return <div key={`el-${i}`} className="h-1" />;
        return <p key={`p-${i}`}>{formatInline(line)}</p>;
      })}
    </div>
  );
}

function formatInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={`b-${i}`}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('*') && part.endsWith('*')) return <em key={`em-${i}`}>{part.slice(1, -1)}</em>;
    if (part.startsWith('`') && part.endsWith('`')) return <code key={`cd-${i}`} className="bg-slate-100 px-1 rounded text-xs">{part.slice(1, -1)}</code>;
    return part;
  });
}

export default function AiChat({ token }) {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [file, setFile] = useState(null);
  const [generateImage, setGenerateImage] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai-chat/sessions`, { headers });
      setSessions(res.data || []);
    } catch (err) { console.error(err); } finally { setLoadingSessions(false); }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const loadMessages = async (sessionId) => {
    try {
      const res = await axios.get(`${API_URL}/api/ai-chat/sessions/${sessionId}/messages`, { headers });
      setMessages(res.data || []);
    } catch {
      toast.error('Failed to load messages');
    }
  };

  const createSession = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/ai-chat/sessions`, {}, { headers });
      setSessions(prev => [res.data, ...prev]);
      setActiveSession(res.data);
      setMessages([]);
      setShowSidebar(false);
    } catch {
      toast.error('Failed to create chat');
    }
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/api/ai-chat/sessions/${sessionId}`, { headers });
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (activeSession?.id === sessionId) {
        setActiveSession(null);
        setMessages([]);
      }
      toast.success('Chat deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const selectSession = (session) => {
    setActiveSession(session);
    loadMessages(session.id);
    setShowSidebar(false);
  };

  const handleSend = async () => {
    if ((!input.trim() && !file) || sending) return;

    setSending(true);
    const formData = new FormData();
    formData.append('session_id', activeSession.id);
    formData.append('message', input.trim());
    formData.append('generate_image', generateImage);
    if (file) formData.append('file', file);

    const tempUserMsg = {
      id: 'temp-' + Date.now(),
      role: 'user',
      content: input.trim(),
      file_url: file ? URL.createObjectURL(file) : null,
      file_type: file?.type,
      file_name: file?.name,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMsg]);
    setInput('');
    setFile(null);
    setGenerateImage(false);

    try {
      const res = await axios.post(`${API_URL}/api/ai-chat/message`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== tempUserMsg.id);
        return [...filtered, res.data.user_message, res.data.ai_message];
      });
      // Update session title in sidebar
      if (sessions.length && res.data.user_message?.content) {
        setSessions(prev => prev.map(s =>
          s.id === activeSession.id
            ? { ...s, title: res.data.user_message.content.slice(0, 60), updated_at: new Date().toISOString() }
            : s
        ));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickPrompts = [
    { label: 'Marketing Plan', prompt: 'Create a 30-day marketing plan for my clinic' },
    { label: 'Blog Post', prompt: 'Write a health awareness blog post about preventive care for my clinic website' },
    { label: 'Social Caption', prompt: 'Write 3 engaging Instagram captions for a dental clinic promoting teeth whitening services' },
    { label: 'Generate Poster', prompt: 'Create a professional clinic health checkup camp poster with modern design', image: true },
  ];

  // Sidebar
  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-slate-200/80">
        <Button
          onClick={createSession}
          className="w-full bg-teal-700 hover:bg-teal-800 text-white"
          size="sm"
          data-testid="new-chat-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> New Chat
        </Button>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {loadingSessions ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : sessions.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">No chats yet</p>
          ) : (
            sessions.map(s => (
              <div
                key={s.id}
                onClick={() => selectSession(s)}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center justify-between group transition-colors cursor-pointer ${
                  activeSession?.id === s.id
                    ? 'bg-teal-50 text-teal-800 border border-teal-200'
                    : 'hover:bg-slate-50 text-slate-600'
                }`}
                data-testid={`chat-session-${s.id}`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{s.title || 'New Chat'}</span>
                </div>
                <button
                  onClick={(e) => deleteSession(s.id, e)}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded transition-opacity"
                  data-testid={`delete-session-${s.id}`}
                >
                  <Trash2 className="w-3 h-3 text-red-400" />
                </button>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );

  return (
    <div className="flex h-[calc(100vh-280px)] min-h-[500px] border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid="ai-chat-container">
      {/* Sidebar - Desktop */}
      <div className="hidden md:flex w-56 border-r border-slate-200/80 bg-slate-50/50 flex-col">
        <SidebarContent />
      </div>

      {/* Mobile sidebar overlay */}
      {showSidebar && (
        <div className="md:hidden absolute inset-0 z-20 bg-white flex flex-col">
          <SidebarContent />
        </div>
      )}

      {/* Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200/80 bg-gradient-to-r from-teal-50/50 to-slate-50">
          <button
            onClick={() => setShowSidebar(!showSidebar)}
            className="md:hidden p-1 hover:bg-slate-100 rounded"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-600 to-emerald-600 flex items-center justify-center">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-800 text-sm">1Clin AI</h3>
            <p className="text-[10px] text-slate-400">Marketing, Blogs, Posters & More</p>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 px-4 py-3">
          {!activeSession ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-100 to-emerald-100 flex items-center justify-center mb-4">
                <Bot className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="font-bold text-slate-800 text-lg mb-1" style={{ fontFamily: 'Manrope' }}>Welcome to 1Clin AI</h3>
              <p className="text-slate-400 text-sm mb-6 max-w-sm">
                Your AI assistant for marketing, content creation, poster design, and 1Clin software help.
              </p>
              <Button onClick={createSession} className="bg-teal-700 hover:bg-teal-800" data-testid="start-chat-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Start a New Chat
              </Button>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-8">
              <p className="text-slate-400 text-sm mb-4">How can I help you today?</p>
              <div className="grid grid-cols-2 gap-2 max-w-md">
                {quickPrompts.map((qp, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setInput(qp.prompt);
                      if (qp.image) setGenerateImage(true);
                    }}
                    className="text-left p-3 bg-slate-50 hover:bg-teal-50 border border-slate-200 hover:border-teal-200 rounded-lg transition-colors"
                    data-testid={`quick-prompt-${idx}`}
                  >
                    <p className="text-xs font-medium text-slate-700">{qp.label}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5 line-clamp-2">{qp.prompt}</p>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map(msg => (
                <div key={msg.id} className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-6 h-6 rounded-md bg-gradient-to-br from-teal-600 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Bot className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                  <div className={`max-w-[80%] rounded-xl px-3.5 py-2.5 ${
                    msg.role === 'user'
                      ? 'bg-teal-700 text-white'
                      : 'bg-slate-100 text-slate-800'
                  }`} data-testid={`chat-message-${msg.id}`}>
                    {/* Uploaded file preview */}
                    {msg.file_url && msg.file_type?.startsWith('image/') && (
                      <img
                        src={msg.file_url.startsWith('blob:') ? msg.file_url : `${API_URL}${msg.file_url}?auth=${token}`}
                        alt="Uploaded"
                        className="rounded-lg max-h-40 mb-2"
                      />
                    )}
                    {msg.file_url && !msg.file_type?.startsWith('image/') && (
                      <div className="flex items-center gap-1.5 mb-2 text-xs opacity-80">
                        <Paperclip className="w-3 h-3" />
                        <span>{msg.file_name || 'File'}</span>
                      </div>
                    )}
                    {msg.content && (
                      msg.role === 'user'
                        ? <p className="text-sm">{msg.content}</p>
                        : <MarkdownText text={msg.content} />
                    )}
                    {/* AI generated image */}
                    {msg.image_url && (
                      <div className="mt-2">
                        <img
                          src={`${API_URL}${msg.image_url}?auth=${token}`}
                          alt="AI Generated"
                          className="rounded-lg max-h-64 border border-slate-200"
                        />
                        <a
                          href={`${API_URL}${msg.image_url}?auth=${token}`}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] text-teal-600 hover:text-teal-700 mt-1"
                        >
                          <Download className="w-3 h-3" /> Download Image
                        </a>
                      </div>
                    )}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-6 h-6 rounded-md bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <User className="w-3.5 h-3.5 text-slate-500" />
                    </div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="flex gap-2.5">
                  <div className="w-6 h-6 rounded-md bg-gradient-to-br from-teal-600 to-emerald-600 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div className="bg-slate-100 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>{generateImage ? 'Generating image...' : 'Thinking...'}</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Input Area */}
        {activeSession && (
          <div className="border-t border-slate-200/80 p-3 bg-white">
            {/* File preview */}
            {file && (
              <div className="flex items-center gap-2 mb-2 p-2 bg-slate-50 rounded-lg">
                {file.type?.startsWith('image/') ? (
                  <img src={URL.createObjectURL(file)} alt="Preview" className="w-10 h-10 rounded object-cover" />
                ) : (
                  <Paperclip className="w-4 h-4 text-slate-400" />
                )}
                <span className="text-xs text-slate-600 flex-1 truncate">{file.name}</span>
                <button onClick={() => setFile(null)} className="p-0.5 hover:bg-slate-200 rounded">
                  <X className="w-3.5 h-3.5 text-slate-400" />
                </button>
              </div>
            )}
            {generateImage && (
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary" className="text-[10px] gap-1">
                  <ImagePlus className="w-3 h-3" /> Image Generation ON
                  <button onClick={() => setGenerateImage(false)} className="ml-1"><X className="w-2.5 h-2.5" /></button>
                </Badge>
              </div>
            )}
            <div className="flex items-end gap-2">
              <div className="flex gap-1">
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-slate-400 hover:text-slate-600"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  data-testid="chat-attach-btn"
                >
                  <Paperclip className="w-4 h-4" />
                </Button>
                <Button
                  variant={generateImage ? 'default' : 'ghost'}
                  size="icon"
                  className={`h-9 w-9 ${generateImage ? 'bg-violet-100 text-violet-700 hover:bg-violet-200' : 'text-slate-400 hover:text-slate-600'}`}
                  onClick={() => setGenerateImage(!generateImage)}
                  disabled={sending}
                  data-testid="chat-image-gen-btn"
                >
                  <ImagePlus className="w-4 h-4" />
                </Button>
              </div>
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask 1Clin AI anything..."
                className="flex-1"
                disabled={sending}
                data-testid="chat-input"
              />
              <Button
                onClick={handleSend}
                disabled={sending || (!input.trim() && !file)}
                className="bg-teal-700 hover:bg-teal-800 h-9 w-9"
                size="icon"
                data-testid="chat-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
